import React, { useState, useEffect, useRef } from 'react';
import { Star, Upload, X } from 'lucide-react';
import instance from '../../helpers/axiosconfig';

const ReviewModal = ({ isOpen, onClose, productId, productName, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setComment('');
      setError('');
      setSelectedImage(null);
      setImagePreview(null);
    }
  }, [isOpen]);

  const validateForm = () => {
    if (!rating) {
      setError('Please select a rating');
      return false;
    }
    if (!comment.trim()) {
      setError('Please write a review comment');
      return false;
    }
    if (comment.length > 500) {
      setError('Review comment must not exceed 500 characters');
      return false;
    }
    return true;
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size should not exceed 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!productId) {
      setError('Product ID is missing');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to submit a review');
        return;
      }

      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Upload image if selected
      let imageName = null;
      if (selectedImage) {
        const formData = new FormData();
        formData.append('images', selectedImage);

        const imageResponse = await instance.post('update-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (imageResponse.data.filenames && imageResponse.data.filenames.length > 0) {
          imageName = imageResponse.data.filenames[0];
        }
      }

      const response = await instance.post('reviews', {
        productId,
        rating,
        comment: comment.trim(),
        image: imageName
      });

      if (response.data.success) {
        onReviewSubmitted();
        onClose();
      } else {
        setError(response.data.message || 'Failed to submit review');
      }
    } catch (err) {
      console.error('Review submission error:', err);
      setError(
        err.response?.data?.message || 
        err.message || 
        'Failed to submit review'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="review-modal-overlay">
      <div className="review-modal">
        <div className="review-modal-header">
          <h2>Add Review</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="review-modal-content">
          <p className="product-name">{productName}</p>
          
          <form onSubmit={handleSubmit}>
            <div className="rating-container">
              <p>Rate this product</p>
              <div className="stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={32}
                    className={`star ${star <= (hoveredRating || rating) ? 'active' : ''}`}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    onClick={() => setRating(star)}
                  />
                ))}
              </div>
              <span className="rating-text">
                {rating ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select rating'}
              </span>
            </div>

            <div className="comment-container">
              <label htmlFor="review-comment">Your Review</label>
              <textarea
                id="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience with this product..."
                required
              />
            </div>

            <div className="image-upload-container">
              <label>Add Photo (Optional)</label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              
              <div className="image-preview-area">
                {!imagePreview ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="upload-button"
                  >
                    <Upload size={20} />
                    <span>Upload Image</span>
                  </button>
                ) : (
                  <div className="preview-container">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="image-preview"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="remove-image"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
              <p className="image-help-text">
                Maximum file size: 5MB. Supported formats: JPG, PNG
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="button-container">
              <button
                type="button"
                className="cancel-button"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-button"
                disabled={!rating || !comment || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;